import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import EligibleAgentTemplateSelect from 'components/EligibleAgentTemplateSelect/EligibleAgentTemplateSelect';
import SidePanel from 'components/SidePanel/SidePanel';
import TextField from 'components/TextField/TextField';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  MONITORING_WEEKDAY_LABELS,
  MONITORING_WEEKDAY_ORDER,
} from '../constants/monitoringScheduleConstants';
import { getMonitoringTimezoneOptions } from '../constants/monitoringTimezones';
import createDefaultScheduleConfigDraft from '../utils/createDefaultScheduleConfigDraft';
import createScheduleConfigDraftFromSchedule from '../utils/createScheduleConfigDraftFromSchedule';
import {
  getLocalIsoDate,
  normalizeTimeOfDay,
} from '../utils/monitoringScheduleDateUtils';
import { buildSchedulePreviewText } from '../utils/scheduleRecurrenceSummary';
import {
  hasMonitoringScheduleValidationErrors,
  validateMonitoringSchedule,
} from '../utils/validateMonitoringSchedule';

import type { TImpactScenarioMinSeverity, TMonitoringScheduleFrequency, TMonitoringWeekday } from 'constants/interfaces/IMonitoringSchedule';
import type IScheduleConfigDraft from '../interfaces/IScheduleConfigDraft';
import type IScheduleConfigPanel from './interfaces/IScheduleConfigPanel';

import styles from './sass/ScheduleConfigPanel.module.scss';

const FREQUENCY_OPTIONS: TMonitoringScheduleFrequency[] = ['daily', 'weekly', 'monthly'];
const IMPACT_MIN_SEVERITY_OPTIONS: TImpactScenarioMinSeverity[] = ['medium', 'high', 'critical'];

function ScheduleConfigPanel({
  open,
  isSaving = false,
  valueChainMapId = 0,
  initialAgentConfigId = null,
  scheduleToEdit,
  agentDefinition,
  onBack,
  onClose,
  onSave,
}: IScheduleConfigPanel): React.JSX.Element {
  const { t } = useTranslation();
  const isEditMode = Boolean(scheduleToEdit);
  const [draft, setDraft] = useState<IScheduleConfigDraft>(createDefaultScheduleConfigDraft);
  const [timezoneFilter, setTimezoneFilter] = useState('');
  const [validationErrors, setValidationErrors] = useState<ReturnType<typeof validateMonitoringSchedule>>({});
  const [agentConfigError, setAgentConfigError] = useState<string | undefined>(undefined);
  const [impactSeverityError, setImpactSeverityError] = useState<string | undefined>(undefined);

  const timezoneOptions = useMemo(() => getMonitoringTimezoneOptions(), []);
  const filteredTimezoneOptions = useMemo(() => {
    const query = timezoneFilter.trim().toLowerCase();

    if (!query) {
      return timezoneOptions;
    }

    return timezoneOptions.filter((option) => (
      option.id.toLowerCase().includes(query)
      || option.label.toLowerCase().includes(query)
    ));
  }, [timezoneFilter, timezoneOptions]);

  const previewText = useMemo(
    () => buildSchedulePreviewText({ schedule: draft, noEndDate: draft.noEndDate }),
    [draft],
  );

  const intervalLabel = useMemo(() => {
    let unit: string;

    if (draft.recurrence.frequency === 'daily') {
      unit = t('valueChainMap.monitoringScheduleIntervalDay', { count: draft.recurrence.interval });
    } else if (draft.recurrence.frequency === 'weekly') {
      unit = t('valueChainMap.monitoringScheduleIntervalWeek', { count: draft.recurrence.interval });
    } else {
      unit = t('valueChainMap.monitoringScheduleIntervalMonth', { count: draft.recurrence.interval });
    }

    return t('valueChainMap.monitoringScheduleIntervalEvery', {
      count: draft.recurrence.interval,
      unit,
    });
  }, [draft.recurrence.frequency, draft.recurrence.interval, t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraft(
      scheduleToEdit
        ? createScheduleConfigDraftFromSchedule(scheduleToEdit)
        : {
          ...createDefaultScheduleConfigDraft(),
          agentConfigId: initialAgentConfigId,
        },
    );
    setTimezoneFilter('');
    setValidationErrors({});
    setAgentConfigError(undefined);
    setImpactSeverityError(undefined);
    // Prefill once per open / schedule id so list cache refreshes do not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: seed from scheduleToEdit.id only
  }, [initialAgentConfigId, open, scheduleToEdit?.id]);

  const updateRecurrence = useCallback((
    patch: Partial<IScheduleConfigDraft['recurrence']>,
  ) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      recurrence: {
        ...currentDraft.recurrence,
        ...patch,
      },
    }));
  }, []);

  const toggleWeekday = useCallback((day: TMonitoringWeekday) => {
    setDraft((currentDraft) => {
      const currentDays = currentDraft.recurrence.daysOfWeek || [];
      const hasDay = currentDays.includes(day);
      const nextDays = hasDay
        ? currentDays.filter((value) => value !== day)
        : [...currentDays, day].sort(
          (left, right) => MONITORING_WEEKDAY_ORDER.indexOf(left)
            - MONITORING_WEEKDAY_ORDER.indexOf(right),
        );

      return {
        ...currentDraft,
        recurrence: {
          ...currentDraft.recurrence,
          daysOfWeek: nextDays,
        },
      };
    });
  }, []);

  const handleSave = useCallback(() => {
    const errors = validateMonitoringSchedule(draft, {
      required: t('valueChainMap.monitoringScheduleValidationRequired'),
      daysOfWeek: t('valueChainMap.monitoringScheduleValidationDaysOfWeek'),
      startDatePast: t('valueChainMap.monitoringScheduleValidationStartDatePast'),
      endDateBeforeStart: t('valueChainMap.monitoringScheduleValidationEndDateBeforeStart'),
      intervalMin: t('valueChainMap.monitoringScheduleValidationIntervalMin'),
      hourOnly: t('valueChainMap.monitoringScheduleValidationHourOnly'),
    }, getLocalIsoDate(), { allowPastStartDate: isEditMode });

    setValidationErrors(errors);

    if (!draft.agentConfigId) {
      setAgentConfigError(t('valueChainMap.agentTemplateRequired'));
      return;
    }

    setAgentConfigError(undefined);

    if (draft.autoCreateImpactScenario && !draft.impactScenarioMinSeverity) {
      setImpactSeverityError(t('valueChainMap.monitoringScheduleImpactSeverityRequired'));
      return;
    }

    setImpactSeverityError(undefined);

    if (hasMonitoringScheduleValidationErrors(errors)) {
      return;
    }

    onSave({
      ...draft,
      recurrence: {
        ...draft.recurrence,
        timeOfDay: normalizeTimeOfDay(draft.recurrence.timeOfDay),
        endDate: draft.noEndDate ? null : draft.recurrence.endDate,
      },
    });
  }, [draft, isEditMode, onSave, t]);

  const panelTitle = isEditMode
    ? t('valueChainMap.monitoringScheduleEditTitle')
    : t('valueChainMap.monitoringScheduleConfigTitle');
  const saveLabelKey = isEditMode
    ? 'valueChainMap.monitoringScheduleUpdate'
    : 'valueChainMap.monitoringScheduleSave';

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={panelTitle}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onBack}
          aria-label={t('common.back')}
          data-i18n-key="common.back"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </button>
        <span className={styles.headerTitle}>{panelTitle}</span>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClose}
          aria-label={t('common.close')}
          data-i18n-key="common.close"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        {agentDefinition?.schedulesInfoKey ? (
          <div className={styles.infoBox}>
            <InfoIcon aria-hidden="true" />
            <p className={styles.infoText}>{t(agentDefinition.schedulesInfoKey)}</p>
          </div>
        ) : null}
        {agentDefinition ? (
          <EligibleAgentTemplateSelect
            valueChainMapId={valueChainMapId}
            type={agentDefinition.sessionType}
            subType={agentDefinition.subType}
            value={draft.agentConfigId}
            onChange={(nextId) => {
              setDraft((currentDraft) => ({
                ...currentDraft,
                agentConfigId: nextId,
              }));
              if (nextId) {
                setAgentConfigError(undefined);
              }
            }}
            className={styles.templateSelect}
            disabled={isSaving}
          />
        ) : null}
        {agentConfigError ? (
          <p className={styles.fieldError}>{agentConfigError}</p>
        ) : null}
        <div className={styles.form}>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleModeRecurring')}</span>
            <div className={styles.segmentedControl}>
              <button
                type="button"
                className={clsx(styles.segmentButton, styles.segmentButtonActive)}
                aria-pressed="true"
              >
                {t('valueChainMap.monitoringScheduleModeRecurring')}
              </button>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <TextField
              name="scheduleTimezoneFilter"
              label={t('valueChainMap.monitoringScheduleTimezone')}
              value={timezoneFilter}
              placeholder={t('valueChainMap.monitoringScheduleTimezoneSearch')}
              onChange={({ target }) => setTimezoneFilter(target.value)}
              error={validationErrors.timezone}
              required
            />
            <select
              className={clsx(styles.timezoneSelect, {
                [styles.timezoneSelectError]: Boolean(validationErrors.timezone),
              })}
              value={draft.timezone}
              onChange={({ target }) => setDraft((currentDraft) => ({
                ...currentDraft,
                timezone: target.value,
              }))}
            >
              {filteredTimezoneOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            {validationErrors.timezone ? (
              <p className={styles.fieldError}>{validationErrors.timezone}</p>
            ) : null}
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleFrequency')}</span>
            <div className={styles.pillGroup}>
              {FREQUENCY_OPTIONS.map((frequency) => {
                const frequencyLabelKey = {
                  daily: 'valueChainMap.monitoringScheduleFrequencyDaily',
                  weekly: 'valueChainMap.monitoringScheduleFrequencyWeekly',
                  monthly: 'valueChainMap.monitoringScheduleFrequencyMonthly',
                }[frequency];

                return (
                  <button
                    key={frequency}
                    type="button"
                    className={clsx(styles.pillButton, {
                      [styles.pillButtonActive]: draft.recurrence.frequency === frequency,
                    })}
                    onClick={() => updateRecurrence({ frequency })}
                  >
                    {t(frequencyLabelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleInterval')}</span>
            <div className={styles.intervalRow}>
              <TextField
                name="scheduleInterval"
                type="number"
                value={String(draft.recurrence.interval)}
                className={styles.intervalInput}
                onChange={({ target }) => {
                  const nextInterval = Number(target.value);

                  updateRecurrence({
                    interval: Number.isFinite(nextInterval) && nextInterval > 0
                      ? Math.floor(nextInterval)
                      : 1,
                  });
                }}
                error={validationErrors.interval}
              />
              <span className={styles.intervalLabel}>{intervalLabel}</span>
            </div>
            {validationErrors.interval ? (
              <p className={styles.fieldError}>{validationErrors.interval}</p>
            ) : null}
          </div>

          {draft.recurrence.frequency === 'weekly' ? (
            <div className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleDaysOfWeek')}</span>
              <div className={styles.dayChipGroup}>
                {MONITORING_WEEKDAY_ORDER.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={clsx(styles.dayChip, {
                      [styles.dayChipActive]: draft.recurrence.daysOfWeek?.includes(day),
                    })}
                    onClick={() => toggleWeekday(day)}
                  >
                    {MONITORING_WEEKDAY_LABELS[day]}
                  </button>
                ))}
              </div>
              {validationErrors.daysOfWeek ? (
                <p className={styles.fieldError}>{validationErrors.daysOfWeek}</p>
              ) : null}
            </div>
          ) : null}

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleTimeOfDay')}</span>
            <input
              className={clsx(styles.nativeInput, {
                [styles.nativeInputError]: Boolean(validationErrors.timeOfDay),
              })}
              type="time"
              step={3600}
              value={draft.recurrence.timeOfDay.slice(0, 5)}
              onChange={({ target }) => updateRecurrence({
                timeOfDay: normalizeTimeOfDay(target.value),
              })}
              aria-describedby="schedule-time-hour-hint"
            />
            <p id="schedule-time-hour-hint" className={styles.fieldHint}>
              {t('valueChainMap.monitoringScheduleTimeOfDayHint')}
            </p>
            {validationErrors.timeOfDay ? (
              <p className={styles.fieldError}>{validationErrors.timeOfDay}</p>
            ) : null}
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleStartDate')}</span>
            <input
              className={clsx(styles.nativeInput, {
                [styles.nativeInputError]: Boolean(validationErrors.startDate),
              })}
              type="date"
              value={draft.recurrence.startDate}
              onChange={({ target }) => updateRecurrence({ startDate: target.value })}
            />
            {validationErrors.startDate ? (
              <p className={styles.fieldError}>{validationErrors.startDate}</p>
            ) : null}
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('valueChainMap.monitoringScheduleEndDate')}</span>
            <input
              className={clsx(styles.nativeInput, {
                [styles.nativeInputError]: Boolean(validationErrors.endDate),
              })}
              type="date"
              value={draft.noEndDate ? '' : (draft.recurrence.endDate || '')}
              disabled={draft.noEndDate}
              onChange={({ target }) => updateRecurrence({ endDate: target.value })}
            />
            <div className={styles.noEndDateRow}>
              <Checkbox
                name="scheduleNoEndDate"
                value="no-end-date"
                label={t('valueChainMap.monitoringScheduleNoEndDate')}
                checked={draft.noEndDate}
                onChange={({ target }) => setDraft((currentDraft) => ({
                  ...currentDraft,
                  noEndDate: target.checked,
                }))}
              />
            </div>
            {validationErrors.endDate ? (
              <p className={styles.fieldError}>{validationErrors.endDate}</p>
            ) : null}
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>
              {t('valueChainMap.monitoringScheduleImpactTitle')}
            </span>
            <p className={styles.fieldHint}>
              {t('valueChainMap.monitoringScheduleImpactHint')}
            </p>
            <div className={styles.noEndDateRow}>
              <Checkbox
                name="scheduleAutoCreateImpact"
                value="auto-create-impact"
                label={t('valueChainMap.monitoringScheduleAutoCreateImpact')}
                checked={draft.autoCreateImpactScenario}
                onChange={({ target }) => setDraft((currentDraft) => ({
                  ...currentDraft,
                  autoCreateImpactScenario: target.checked,
                  impactScenarioMinSeverity: target.checked
                    ? (currentDraft.impactScenarioMinSeverity ?? 'high')
                    : null,
                }))}
              />
            </div>
            {draft.autoCreateImpactScenario ? (
              <>
                <span className={styles.fieldLabel}>
                  {t('valueChainMap.monitoringScheduleImpactMinSeverity')}
                </span>
                <div className={styles.pillGroup}>
                  {IMPACT_MIN_SEVERITY_OPTIONS.map((severity) => (
                    <button
                      key={severity}
                      type="button"
                      className={clsx(styles.pillButton, {
                        [styles.pillButtonActive]: draft.impactScenarioMinSeverity === severity,
                      })}
                      onClick={() => setDraft((currentDraft) => ({
                        ...currentDraft,
                        impactScenarioMinSeverity: severity,
                      }))}
                    >
                      {t(`valueChainMap.monitoringFindingSeverity${severity[0].toUpperCase()}${severity.slice(1)}`)}
                    </button>
                  ))}
                </div>
                {impactSeverityError ? (
                  <p className={styles.fieldError}>{impactSeverityError}</p>
                ) : null}
              </>
            ) : null}
          </div>

          <div className={styles.previewBox}>
            <p className={styles.previewLabel}>{t('valueChainMap.monitoringSchedulePreview')}</p>
            <p className={styles.previewText}>{previewText}</p>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          title={t(saveLabelKey)}
          labelKey={saveLabelKey}
          variant={BUTTON_VARIANT.primary}
          onClick={handleSave}
          className={styles.footerButton}
          disabled={isSaving}
        />
        <Button
          title={t('common.back')}
          variant={BUTTON_VARIANT.secondary}
          onClick={onBack}
          className={styles.footerButton}
          disabled={isSaving}
        />
      </div>
    </SidePanel>
  );
}

export default ScheduleConfigPanel;
